import { useEffect, useState } from 'react';
import axios from 'axios';
import { searchPets } from '../api/petApi';
import { adaptPets } from '../api/petAdapter';
import type { Pet, SearchPetsParams } from '../types';

export interface UsePetsResult {
  pets: Pet[];
  page: number;
  totalPages: number;
  totalElements: number;
  loading: boolean;
  error: Error | null;
}

const EMPTY: UsePetsResult = {
  pets: [],
  page: 0,
  totalPages: 0,
  totalElements: 0,
  loading: false,
  error: null,
};

/**
 * Fetches the paginated pet search and adapts each row into the editorial
 * Pet shape. Cancels in-flight requests on param change / unmount.
 *
 * Notes:
 * - Backend pagination is 0-based.
 * - Public /search already returns only AVAILABLE (non-archived, non-hidden)
 *   pets, so totals and page contents are server-authoritative — no client-side
 *   status filtering here (that would desync `pets.length` from `totalElements`).
 */
export function usePets(params: SearchPetsParams): UsePetsResult {
  const [state, setState] = useState<UsePetsResult>({ ...EMPTY, loading: true });

  // Stringify so that referentially-distinct objects with identical content
  // don't cause re-fetches.
  const key = JSON.stringify(params);

  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));

    searchPets(params, ctrl.signal)
      .then((page) => {
        if (cancelled) return;
        setState({
          pets: adaptPets(page.content),
          page: page.number,
          totalPages: page.totalPages,
          totalElements: page.totalElements,
          loading: false,
          error: null,
        });
      })
      .catch((err: unknown) => {
        if (cancelled || axios.isCancel(err)) return;
        if (axios.isAxiosError(err) && err.code === 'ERR_CANCELED') return;
        setState({
          ...EMPTY,
          loading: false,
          error: err instanceof Error ? err : new Error(String(err)),
        });
      });

    return () => {
      cancelled = true;
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  return state;
}
